import React from 'react';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import ErrorBoundary from './components/ErrorBoundary';
import Login from './components/Login';
import Register from './components/Auth/Register';
import Dashboard from './components/Dashboard';
import Onboarding from './components/Onboarding/Onboarding';
import VerificationCompletePage from './pages/VerificationCompletePage';
import Documents from './components/Documents';
import Vehicles from './components/Vehicles';
import VehicleDetails from './components/Vehicles/VehicleDetails';
import Drivers from './components/Drivers';
import Authority from './components/Authority';
import CompliancePassport from './components/CompliancePassport';
import PublicPassportView from './components/Passport/PublicPassportView';
import Sidebar from './components/Sidebar';
import ChatBotButton from './components/ChatBot/ChatBotButton';
import InsuranceLeads from './components/Admin/InsuranceLeads';
import PricingPage from './components/Subscriptions/PricingPage';
import ClearinghouseInfo from './components/Clearinghouse/ClearinghouseInfo';
import QueryTracker from './components/Clearinghouse/QueryTracker';
import IFTAPage from './components/IFTA/IFTAPage';
import AuditChecklistPage from './components/Audit/AuditChecklistPage';
import ComplianceTimelinePage from './components/ComplianceTimeline/ComplianceTimelinePage';
import ViolationsPage from './components/Violations/ViolationsPage';
import CounterpartyViewPage from './components/Counterparty/CounterpartyViewPage';
import AlertsPage from './components/Alerts/AlertsPage';
import ChecklistsPage from './components/Checklists/ChecklistsPage';
import AuthorityCopilotPage from './components/AuthorityCopilot/AuthorityCopilotPage';
import AuthorityCopilotPlanPage from './components/AuthorityCopilot/AuthorityCopilotPlanPage';
import AnalyticsPage from './components/Analytics/AnalyticsPage';
import Settings from './components/Settings';
import Topbar from './components/Topbar';
import PassportPage from './pages/PassportPage';
import AdminPage from './pages/AdminPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import LandingPage from './pages/LandingPage';
import LandingV2 from './pages/LandingV2';
import BlogIndexPage from './pages/BlogIndexPage';
import BlogPostPage from './pages/BlogPostPage';
import RouteSeo from './seo/routeMeta';
import SupportPage from './pages/SupportPage';
import SmsConsentPage from './pages/SmsConsentPage';
import FleetDriverProfilePage from './pages/DriverProfile';
import './theme/index.css';
import './components/ui/ui.css';
import './theme/shell.css';
import './App.css';
import './onboarding-contrast.css';

function AppContent() {
  const location = useLocation();
  const token = localStorage.getItem('token');
  const isAuthenticated = !!token;
  const hideOnPages = ['/', '/register', '/onboarding', '/terms', '/privacy', '/sms-consent', '/fc', '/landing-v2', '/landing-legacy'];
  const showChatBot = !hideOnPages.includes(location.pathname);

  // Public shareable passport link (no auth)
  if (location.pathname.startsWith('/p/')) {
    return (
      <div className="App">
        <Routes>
          <Route path="/p/:shareToken" element={<PublicPassportView />} />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </div>
    );
  }

  // Public Compliance Passport™ by passport id (e.g. SVN-XXXXXX)
  if (/^\/passport\/.+/.test(location.pathname)) {
    return (
      <div className="App">
        <Routes>
          <Route path="/passport/:passportId" element={<PassportPage />} />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </div>
    );
  }

  const publicShellPaths = [
    '/',
    '/landing',
    '/landing-legacy',
    '/landing-v2',
    '/login',
    '/register',
    '/onboarding',
    '/onboarding/verification-complete',
    '/forgot-password',
    '/reset-password',
    '/terms',
    '/privacy',
    '/sms-consent',
  ];
  const isBlogPath =
    location.pathname === '/blog' || location.pathname.startsWith('/blog/');
  const isPublicShell =
    !isAuthenticated || publicShellPaths.includes(location.pathname) || isBlogPath;

  if (isPublicShell) {
    return (
      <div className="App">
        <Routes>
          <Route
            path="/"
            element={
              isAuthenticated ? <Navigate to="/dashboard" replace /> : <LandingV2 />
            }
          />
          <Route
            path="/landing"
            element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <LandingV2 />}
          />
          <Route
            path="/landing-legacy"
            element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <LandingPage />}
          />
          <Route
            path="/landing-v2"
            element={<Navigate to="/" replace />}
          />
          <Route
            path="/login"
            element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <Login />}
          />
          <Route
            path="/register"
            element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <Register />}
          />
          <Route path="/onboarding" element={<Onboarding />} />
          <Route
            path="/onboarding/verification-complete"
            element={<VerificationCompletePage />}
          />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/sms-consent" element={<SmsConsentPage />} />
          <Route path="/blog" element={<BlogIndexPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
    );
  }

  // Authenticated routes (with sidebar + topbar)
  return (
    <div className="App">
      <div className="app-layout">
        <Sidebar />
        <div className="main-content">
          <Topbar />
          <main className="page-content">
          <Routes>
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/documents" element={<Documents />} />
            <Route path="/vehicles" element={<Vehicles />} />
            <Route path="/vehicles/:id" element={<VehicleDetails />} />
            <Route path="/drivers" element={<Drivers />} />
            <Route path="/drivers/:id" element={<FleetDriverProfilePage />} />
            <Route path="/fleet/drivers/:driverId" element={<FleetDriverProfilePage />} />
            <Route path="/authority" element={<Authority />} />
            <Route path="/authority-copilot" element={<AuthorityCopilotPage />} />
            <Route path="/authority-copilot/plan" element={<AuthorityCopilotPlanPage />} />
            <Route path="/passport" element={<CompliancePassport />} />
            <Route path="/p/:shareToken" element={<PublicPassportView />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/clearinghouse" element={<ClearinghouseInfo />} />
            <Route path="/clearinghouse/queries" element={<QueryTracker />} />
            <Route path="/ifta" element={<IFTAPage />} />
            <Route path="/audit" element={<AuditChecklistPage />} />
            <Route path="/timeline" element={<ComplianceTimelinePage />} />
            <Route path="/alerts" element={<AlertsPage />} />
            <Route path="/violations" element={<ViolationsPage />} />
            <Route path="/broker-view" element={<CounterpartyViewPage />} />
            <Route path="/counterparty" element={<CounterpartyViewPage />} />
            <Route path="/checklists" element={<ChecklistsPage />} />
            <Route path="/analytics" element={<AnalyticsPage />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/support" element={<SupportPage />} />
            <Route path="/fc/insurance-leads" element={<InsuranceLeads />} />
            <Route path="/fc" element={<AdminPage />} />
            <Route path="*" element={<Navigate to="/dashboard" />} />
          </Routes>
          </main>
        </div>
      </div>
      
      {/* Global floating chatbot - shows on authenticated pages */}
      {showChatBot && <ChatBotButton />}
    </div>
  );
}

function AppWithToasts() {
  const { resolvedTheme } = useTheme();
  return (
    <>
      <RouteSeo />
      <AppContent />
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme={resolvedTheme}
      />
    </>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <BrowserRouter>
          <ThemeProvider>
            <AppWithToasts />
          </ThemeProvider>
        </BrowserRouter>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;
